import React from "react";
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { useMutation } from "convex/react";
import { useContext } from "react";
import { api } from "../../../../convex/_generated/api";
import { Id } from "../../../../convex/_generated/dataModel";
import { MainRoutes } from "../../../../shared/models";
import { BaseContext } from "../../../../shared/base-context";

interface Session {
  id: string;
  display_name: string;
  description?: string;
  skill_name?: string;
}

interface Props {
  data: {
    session: Session;
    reasoning?: string;
  };
}

export const SessionRecommendationWidget: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { convexUserId } = useContext(BaseContext);
  const startSession = useMutation(api.sessions.startSession);
  const activeSession = useQuery(
    api.user_progress.getCurrentSessionInProgress,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );
  const hasActiveSession = !!activeSession;

  const handleStartSession = async (sessionId: string) => {
    if (!convexUserId) return;

    try {
      const result = await startSession({
        user_id: convexUserId as Id<"users">,
        assistant_id: sessionId as Id<"assistants">,
      });

      if (result?.session_id) {
        navigate(`${MainRoutes.Chat}?sessionId=${result.session_id}`);
      }
    } catch (error) {
    }
  };

  const handleResumeSession = () => {
    if (activeSession?.session_id) {
      navigate(`${MainRoutes.Chat}?sessionId=${activeSession.session_id}`);
    }
  };

  if (!data?.session) {
    return null;
  }

  const { session } = data;

  return (
    <div className="bg-card rounded-xl border-2 border-border/50 p-4 mt-2 shadow-sm">
      <h4 className="font-semibold text-foreground mb-3">{t('widgets.tryThese')}</h4>

      {data.reasoning && (
        <p className="text-sm text-muted-foreground mb-3">{data.reasoning}</p>
      )}

      <div
        className="flex items-center justify-between p-4 bg-muted/50 rounded-xl border border-border/30"
      >
        <div className="flex-1">
          <p className="font-medium text-foreground">{session.display_name}</p>
          {session.skill_name && (
            <p className="text-xs text-muted-foreground">{session.skill_name}</p>
          )}
          {session.description && (
            <p className="text-sm text-muted-foreground mt-1">{session.description}</p>
          )}
        </div>
        {!hasActiveSession ? (
          <button
            onClick={() => handleStartSession(session.id)}
            className="ml-3 px-4 py-2 bg-primary text-white text-sm rounded-full hover:bg-primary/90 transition-all flex-shrink-0 shadow-sm"
          >
            {t('widgets.start')}
          </button>
        ) : null}
      </div>
      {hasActiveSession && (
        <p className="text-xs text-muted-foreground mt-3 text-center">
          Complete your{" "}
          <button
            onClick={handleResumeSession}
            className="underline text-primary hover:text-primary/80"
          >
            {t('widgets.currentSession')}
          </button>
          {" "}first to start a new one.
        </p>
      )}
    </div>
  );
};
